import React, { useEffect, useState, forwardRef, useCallback } from 'react';
import { Table, Button, Card, Spinner, Alert, Form, Modal, OverlayTrigger, Tooltip, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faEye, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useSelector, useDispatch } from 'react-redux';
import { fetchSongModels, deleteSongModels } from '../../redux/actions/songModelActions';
import { fetchCategories } from '../../redux/actions/categoryActions';
import Pagination from '../common/Pagination';
import ImageModal from '../common/ImageModal';
import SongModelForm from './SongModelForm';
import { LANGUAGE_MAPPING, LANGUAGE_NAMES } from '../../utils/languageConstants';
import { hasEditPermission, hasDeletePermission } from '../../utils/authConfig';
import {
  ListDesktop,
  ListMobile,
  MobileCard,
  MobileCardTop,
  MobileCardTitle,
  MobileCardRow,
  MobileCardFooter,
} from '../common/ListMobileLayout';

// Add debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

const SongModels = () => {
  const dispatch = useDispatch();
  const { items, total, loading, error } = useSelector((state) => ({
    items: state.songModels.items,
    total: state.songModels.total,
    loading: state.songModels.loading,
    error: state.songModels.error
  }));

  const { items: categories = [], loading: loadingCategories, error: categoryError } = useSelector((state) => ({
    items: state.categories?.items || [],
    loading: state.categories?.loading || false,
    error: state.categories?.error || null
  }));
  
  // Initialize state from localStorage if available
  const getInitialState = (key, defaultValue) => {
    try {
      const savedValue = localStorage.getItem(`song_models_filter_${key}`);
      return savedValue !== null ? JSON.parse(savedValue) : defaultValue;
    } catch (error) {
      console.error(`Error loading ${key} from localStorage:`, error);
      return defaultValue;
    }
  };

  const [currentPage, setCurrentPage] = useState(getInitialState('currentPage', 1));
  const [selectedCategory, setSelectedCategory] = useState(getInitialState('selectedCategory', ''));
  const [filterType, setFilterType] = useState(getInitialState('filterType', ''));
  const [categoryId, setCategoryId] = useState(getInitialState('categoryId', ''));
  const [language, setLanguage] = useState(getInitialState('language', '3'));
  const [searchInput, setSearchInput] = useState(getInitialState('searchInput', ''));
  const [sortDirection, setSortDirection] = useState(getInitialState('sortDirection', ''));
  const [serviceType, setServiceType] = useState(getInitialState('serviceType', ''));
  const itemsPerPage = 10;
  
  // Other state not related to filtering
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const debouncedSearch = useDebounce(searchInput, 500); // 500ms delay
  const [showSongModelForm, setShowSongModelForm] = useState(false);
  const [editingSongModel, setEditingSongModel] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [songModelToDelete, setSongModelToDelete] = useState(null);
  const [deletingSongModel, setDeletingSongModel] = useState(false);

  // Check permissions
  const canEdit = hasEditPermission();
  const canDelete = hasDeletePermission();

  // Save filter to localStorage when changed
  useEffect(() => {
    try {
      localStorage.setItem('song_models_filter_currentPage', JSON.stringify(currentPage));
      localStorage.setItem('song_models_filter_selectedCategory', JSON.stringify(selectedCategory));
      localStorage.setItem('song_models_filter_filterType', JSON.stringify(filterType));
      localStorage.setItem('song_models_filter_categoryId', JSON.stringify(categoryId));
      localStorage.setItem('song_models_filter_language', JSON.stringify(language));
      localStorage.setItem('song_models_filter_searchInput', JSON.stringify(searchInput));
      localStorage.setItem('song_models_filter_sortDirection', JSON.stringify(sortDirection));
      localStorage.setItem('song_models_filter_serviceType', JSON.stringify(serviceType));
    } catch (error) {
      console.error('Error saving filters to localStorage:', error);
    }
  }, [currentPage, selectedCategory, filterType, categoryId, language, searchInput, sortDirection, serviceType]);

  useEffect(() => {
    dispatch(fetchCategories({page: 1, items_per_page: 100}));
  }, [dispatch]);

  useEffect(() => {
    const params = {
      type: filterType || '',
      language: LANGUAGE_MAPPING[language] || '',
      category_id: categoryId || '',
      is_admin: 1,
      search: debouncedSearch || '',
      ...(LANGUAGE_MAPPING[language] === 'en' && { language: 'en' }),
      ...(sortDirection && {
        sort_by_field: 'reading_time',
        sort_direction: sortDirection
      }),
      ...(serviceType !== '' && { is_backup: serviceType })
    };
    dispatch(fetchSongModels(currentPage, itemsPerPage, params));
  }, [currentPage, filterType, categoryId, language, debouncedSearch, sortDirection, serviceType, dispatch]);

  const handleCategoryChange = (e) => {
    const selectedValue = e.target.value;
    if (!selectedValue) {
      setFilterType('');
      setCategoryId('');
      setSelectedCategory('');
    } else {
      // Find the selected category object
      const selectedCategoryObj = categories.find(cat => cat.code === selectedValue);
      if (selectedCategoryObj) {
        if (selectedCategoryObj.type === 'sub_category') {
          // If it's a subcategory, set type to 'sub_category' and use the subcategory code
          setFilterType('sub_category_code');
          setCategoryId(selectedCategoryObj.code);
        } else {
          // If it's a main category, set type to 'category' and use the category code
          setFilterType('category_code');
          setCategoryId(selectedCategoryObj.code);
        }
        setSelectedCategory(selectedValue);
      }
    }
    setCurrentPage(1); // Reset to first page when changing category
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleImageClick = (imageUrl, songModelName) => {
    setSelectedImage({ url: imageUrl, name: songModelName });
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const handleAddSongModel = () => {
    setEditingSongModel(null);
    setShowSongModelForm(true);
  };

  const handleEditSongModel = (songModel) => {
    setEditingSongModel(songModel);
    setShowSongModelForm(true);
  };

  const handleSongModelFormClose = () => {
    setShowSongModelForm(false);
    setEditingSongModel(null);
  };

  const handleSongModelSaveSuccess = () => {
    setShowSongModelForm(false);
    setEditingSongModel(null);
    // Refresh the songModel list
    const params = {
      type: filterType || '',
      language: LANGUAGE_MAPPING[language] || '',
      category_id: categoryId || '',
      is_admin: 1,
      search: debouncedSearch || '',
      ...(LANGUAGE_MAPPING[language] === 'en' && { language: 'en' }),
      ...(sortDirection && {
        sort_by_field: 'reading_time',
        sort_direction: sortDirection
      }),
      ...(serviceType !== '' && { is_backup: serviceType })
    };
    dispatch(fetchSongModels(currentPage, itemsPerPage, params));
  };

  const handleDeleteClick = (songModel) => {
    setSongModelToDelete(songModel);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSongModelToDelete(null);
  };

  const setSelectedSongModel = (songModel) => {
    setSelectedSongModel(songModel);
  };

  const handleConfirmDelete = async () => {
    if (!songModelToDelete) return;
    
    setDeletingSongModel(true);
    try {
      await dispatch(deleteSongModels(songModelToDelete.id));
      setShowDeleteModal(false);
      setSongModelToDelete(null);
      
      // Refresh the songModel list
      const params = {
        type: filterType || '',
        language: LANGUAGE_MAPPING[language] || '',
        category_id: categoryId || '',
        is_admin: 1,
        search: debouncedSearch || '',
        ...(LANGUAGE_MAPPING[language] === 'en' && { language: 'en' }),
        ...(sortDirection && {
          sort_by_field: 'reading_time',
          sort_direction: sortDirection
        }),
        ...(serviceType !== '' && { is_backup: serviceType })
      };
      dispatch(fetchSongModels(currentPage, itemsPerPage, params));
    } catch (error) {
      console.error('Error deleting song model:', error);
    } finally {
      setDeletingSongModel(false);
    }
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchInput('');
    setLanguage('3');
    setServiceType('');
    setSelectedCategory('');
    setFilterType('');
    setCategoryId('');
    setSortDirection('');
    setCurrentPage(1);
  };

  if (error) {
    return (
      <Alert variant="danger" className="mt-3">
        {error}
      </Alert>
    );
  }

  return (
    <div className="dashboard-list-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>SongModels</h2>
        {canEdit && (
          <Button variant="primary" onClick={handleAddSongModel}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add New Song Model
          </Button>
        )}
      </div>
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex gap-3 flex-wrap">
              <InputGroup style={{ width: '300px' }}>
                <Form.Control
                  placeholder="Search by model name..."
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                    setCurrentPage(1); // Reset to first page when searching
                  }}
                />
                {searchInput && (
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => {
                      setSearchInput('');
                      setCurrentPage(1);
                    }}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
              <Button 
                variant="outline-secondary" 
                onClick={handleResetFilters}
                title="Reset all filters"
              >
                Reset
              </Button>
            </div>
          </div>
          <div className="list-pagination-top">
            <Pagination
              currentPage={currentPage}
              totalItems={total}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              position="top"
            />
          </div>

          <ListDesktop>
            <Table hover className="songModels-table mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Model Id</th>
                  <th>Model Name</th>
                  <th>Model Url</th>
                  <th>Model Thumbnail</th>
                  <th>Categories</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                      </div>
                    </td>
                  </tr>
                ) : (
                  items?.map((songModel) => (
                    <tr key={songModel.id}>
                      <td>{songModel.id}</td>
                      <td>{songModel.model_id}</td>
                      <td>{songModel.model_name}</td>
                      <td>{songModel.model_url}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          {songModel.model_thumbnail && (
                            <img
                              src={songModel.model_thumbnail}
                              alt={songModel.model_name}
                              className="me-2"
                              style={{ width: '40px', height: '60px', objectFit: 'cover', cursor: 'pointer' }}
                              onClick={() => handleImageClick(songModel.model_thumbnail, songModel.name)}
                            />
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          {songModel?.categories?.map((category) => (
                            <span key={category.id} className="badge bg-primary me-2">{category.category_name}</span>
                          ))}
                        </div>
                      </td>
                      <td>
                        {canEdit && (
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="me-2 mt-2"
                            onClick={() => handleEditSongModel(songModel)}
                            title="Edit Song Model"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                        )}
                        {canDelete && (
                          <Button 
                            variant="outline-danger" 
                            size="sm" 
                            className="mt-2"
                            onClick={() => handleDeleteClick(songModel)}
                            title="Delete SongModel"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </ListDesktop>

          {!loading && items?.length > 0 && (
            <ListMobile>
              {items.map((songModel) => (
                <MobileCard key={songModel.id}>
                  <MobileCardTop>
                    {songModel.model_thumbnail ? (
                      <img
                        src={songModel.model_thumbnail}
                        alt={songModel.model_name}
                        style={{ width: 40, height: 60, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                        onClick={() => handleImageClick(songModel.model_thumbnail, songModel.model_name)}
                      />
                    ) : null}
                    <MobileCardTitle>{songModel.model_name}</MobileCardTitle>
                  </MobileCardTop>
                  <MobileCardRow label="ID">{songModel.id}</MobileCardRow>
                  <MobileCardRow label="Model ID" mono>
                    {songModel.model_id}
                  </MobileCardRow>
                  <MobileCardRow label="URL" mono>
                    {songModel.model_url || '—'}
                  </MobileCardRow>
                  <MobileCardRow label="Categories">
                    {songModel?.categories?.length
                      ? songModel.categories.map((category) => (
                          <span key={category.id} className="badge bg-primary me-1 mb-1">
                            {category.category_name}
                          </span>
                        ))
                      : '—'}
                  </MobileCardRow>
                  {(canEdit || canDelete) && (
                    <MobileCardFooter>
                      {canEdit && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditSongModel(songModel)}
                          title="Edit Song Model"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteClick(songModel)}
                          title="Delete SongModel"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      )}
                    </MobileCardFooter>
                  )}
                </MobileCard>
              ))}
            </ListMobile>
          )}

          <Pagination
            currentPage={currentPage}
            totalItems={total}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            position="bottom"
          />
        </Card.Body>
      </Card>

      <ImageModal
        show={showImageModal}
        onHide={handleCloseImageModal}
        imageUrl={selectedImage?.url}
        title={selectedImage?.name}
      />

      {canEdit && (
        <Modal
          show={showSongModelForm}
          onHide={handleSongModelFormClose}
          backdrop={true}
          keyboard={true}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>{editingSongModel ? 'Edit Song Model' : 'Create New SongModel'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SongModelForm 
              songModel={editingSongModel}
              onCancel={handleSongModelFormClose}
              onSuccess={handleSongModelSaveSuccess}
            />
          </Modal.Body>
        </Modal>
      )}

      {canDelete && (
        <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered scrollable fullscreen="sm-down">
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete the song model "{songModelToDelete?.model_name}"?
            This action cannot be undone.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={deletingSongModel}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={deletingSongModel}>
              {deletingSongModel ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default SongModels; 